import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle, XCircle, AlertTriangle, Camera, FileText, Download, Shield, Lock, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const formatCPF = (v) => {
  const n = v.replace(/\D/g, '').slice(0, 11);
  return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};
const unformatCPF = (v) => v.replace(/\D/g, '');

const formatarData = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '-';

export default function EspelhoVisualizarPage() {
  const { token } = useParams();
  const [step, setStep] = useState('auth');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [cpf, setCpf] = useState('');
  const [codigo, setCodigo] = useState('');
  const [espelho, setEspelho] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acao, setAcao] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [selfieBase64, setSelfieBase64] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [concluido, setConcluido] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraAtiva, setCameraAtiva] = useState(false);

  useEffect(() => {
    if (!token) { setError('Link inválido'); setLoading(false); return; }
    setLoading(false);
  }, [token]);

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!cpf || !codigo) { setAuthError('Preencha o CPF e o código'); return; }
    setAuthLoading(true);
    setAuthError(null);
    try {
      const r = await axios.post(`${API}/api/espelho/verificar`, {
        token,
        cpf: unformatCPF(cpf),
        codigo
      });
      setEspelho(r.data);
      setStep('espelho');
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Credenciais inválidas');
    } finally {
      setAuthLoading(false);
    }
  };

  const iniciarCamera = async () => {
    try {
      setCameraAtiva(true);
      await new Promise(r => setTimeout(r, 100));
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (e) {
      setCameraAtiva(false);
      alert('Não foi possível acessar a câmera. Verifique as permissões.');
    }
  };

  const capturarSelfie = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const base64 = canvas.toDataURL('image/jpeg', 0.8);
    setSelfieBase64(base64);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraAtiva(false);
  };

  const handleAssinar = async () => {
    if (!selfieBase64) { alert('Tire uma selfie primeiro'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/espelho/assinar`, { token, selfie_base64: selfieBase64 });
      setConcluido({ tipo: 'assinado', pdf_url: r.data.pdf_url });
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro ao assinar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContestar = async () => {
    if (!motivo || motivo.length < 10) { alert('Descreva o motivo da contestação (mínimo 10 caracteres)'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/espelho/contestar`, { token, motivo });
      setConcluido({ tipo: 'contestado' });
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro ao contestar');
    } finally {
      setSubmitting(false);
    }
  };

  const formatarPeriodo = () => espelho ? `${formatarData(espelho.periodo_inicio)} a ${formatarData(espelho.periodo_fim)}` : '';

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-gray-100"><p className="text-gray-500">Carregando...</p></div>;
  if (error) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <Card className="max-w-md w-full mx-4">
        <CardHeader className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-2" />
          <CardTitle>Link inválido</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );

  if (step === 'auth') return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <Shield className="h-12 w-12 text-blue-600 mx-auto mb-2" />
          <CardTitle>Acesso ao Espelho de Ponto</CardTitle>
          <CardDescription>Informe seu CPF e o código recebido por e-mail</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <Label htmlFor="cpf">CPF</Label>
              <Input id="cpf" value={cpf} onChange={e => setCpf(formatCPF(e.target.value))} placeholder="000.000.000-00" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="codigo">Código de Verificação</Label>
              <Input id="codigo" value={codigo} onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" className="mt-1 text-center text-lg font-mono tracking-widest" maxLength={6} />
            </div>
            {authError && <p className="text-red-600 text-sm text-center">{authError}</p>}
            <Button type="submit" disabled={authLoading || !cpf || codigo.length < 6} className="w-full">
              {authLoading ? 'Verificando...' : 'Acessar Espelho'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );

  if (concluido) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          {concluido.tipo === 'assinado' ? (
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-2" />
          ) : (
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-2" />
          )}
          <CardTitle>
            {concluido.tipo === 'assinado' ? 'Espelho Assinado com Sucesso!' : 'Contestação Registrada'}
          </CardTitle>
          <CardDescription>
            {concluido.tipo === 'assinado'
              ? 'Seu espelho de ponto foi assinado eletronicamente.'
              : 'Sua contestação foi registrada. O RH será notificado.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-2">
          {concluido.tipo === 'assinado' && concluido.pdf_url && (
            <Button onClick={() => window.open(`${API}/api/espelho/${token}/pdf`, '_blank')} className="bg-green-600">
              <Download className="h-4 w-4 mr-1" /> Baixar PDF Assinado
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const tot = espelho.totalizadores;
  const dias = espelho.registros_diarios || [];

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-4xl mx-auto space-y-4">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-blue-600" />
              <div>
                <CardTitle>Espelho de Ponto</CardTitle>
                <CardDescription>
                  {espelho.funcionario_nome} — {formatarPeriodo()}
                </CardDescription>
              </div>
              {espelho.pdf_url && (
                <Button onClick={() => window.open(`${API}/api/espelho/${token}/pdf`, '_blank')} variant="outline" size="sm" className="ml-auto">
                  <Download className="h-4 w-4 mr-1" /> Baixar PDF
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><strong>Empresa:</strong> {espelho.empresa_nome}</div>
              <div><strong>CPF:</strong> {espelho.funcionario_cpf}</div>
              <div><strong>Cargo:</strong> {espelho.funcionario_cargo}</div>
              <div><strong>Jornada:</strong> {espelho.jornada_padrao}</div>
            </div>
          </CardContent>
        </Card>

        {tot && (
          <Card>
            <CardHeader><CardTitle className="text-lg">Totalizadores</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-700">{tot.total_horas_trabalhadas}</div>
                  <div className="text-xs text-gray-600">Horas Trabalhadas</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-700">{tot.total_horas_extras_diurnas}</div>
                  <div className="text-xs text-gray-600">Horas Extras</div>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-yellow-700">{tot.total_atrasos}</div>
                  <div className="text-xs text-gray-600">Atrasos</div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-700">{tot.dias_faltosos}</div>
                  <div className="text-xs text-gray-600">Faltas (dias)</div>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-700">{tot.saldo_acumulado_banco_horas}</div>
                  <div className="text-xs text-gray-600">Saldo Banco de Horas</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {dias.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-lg">Registros Diários ({dias.length} dias)</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2 font-medium">Data</th>
                      <th className="pb-2 font-medium">Dia</th>
                      <th className="pb-2 font-medium">Marcações</th>
                      <th className="pb-2 font-medium text-right">Trab.</th>
                      <th className="pb-2 font-medium text-right">Extras</th>
                      <th className="pb-2 font-medium text-right">Atrasos</th>
                      <th className="pb-2 font-medium text-right">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dias.map((d, idx) => (
                      <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-1.5">{formatarData(d.data)}</td>
                        <td className="py-1.5 text-gray-500">{d.dia_semana?.slice(0, 3)}</td>
                        <td className="py-1.5 text-xs font-mono text-gray-600">
                          {d.marcacoes?.length ? d.marcacoes.join(' · ') : '-'}
                        </td>
                        <td className="py-1.5 text-right">{d.horas_trabalhadas || '00:00'}</td>
                        <td className="py-1.5 text-right text-green-600">{d.horas_extras || '00:00'}</td>
                        <td className="py-1.5 text-right text-red-600">{d.atrasos || '00:00'}</td>
                        <td className="py-1.5 text-right font-medium">{d.saldo_dia || '00:00'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {espelho.status === 'PENDENTE' && !acao && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="cursor-pointer hover:ring-2 hover:ring-green-500 transition-all" onClick={() => setAcao('assinar')}>
              <CardHeader className="text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                <CardTitle className="text-lg">Assinar Espelho</CardTitle>
                <CardDescription>Confirmo que os dados estão corretos</CardDescription>
              </CardHeader>
            </Card>
            <Card className="cursor-pointer hover:ring-2 hover:ring-red-500 transition-all" onClick={() => setAcao('contestar')}>
              <CardHeader className="text-center">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-2" />
                <CardTitle className="text-lg">Contestar</CardTitle>
                <CardDescription>Discordo dos dados apresentados</CardDescription>
              </CardHeader>
            </Card>
          </div>
        )}

        {acao === 'assinar' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Assinar Espelho</CardTitle>
              <CardDescription>Tire uma selfie para confirmar sua assinatura</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <video ref={videoRef} className={`w-full rounded-lg bg-black ${cameraAtiva ? '' : 'hidden'}`} style={{ maxHeight: 300 }} autoPlay playsInline muted />
              {!selfieBase64 ? (
                <div>
                  {!cameraAtiva ? (
                    <Button onClick={iniciarCamera} className="w-full">
                      <Camera className="h-4 w-4 mr-2" /> Abrir Câmera
                    </Button>
                  ) : (
                    <Button onClick={capturarSelfie} className="w-full">Capturar Selfie</Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <img src={selfieBase64} alt="Selfie" className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-green-500" />
                  <div className="flex gap-2">
                    <Button onClick={() => { setSelfieBase64(''); setCameraAtiva(false); }} variant="outline" className="flex-1">Tirar Novamente</Button>
                    <Button onClick={handleAssinar} disabled={submitting} className="flex-1 bg-green-600">
                      {submitting ? 'Assinando...' : 'Confirmar Assinatura'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {acao === 'contestar' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Contestar Espelho</CardTitle>
              <CardDescription>Descreva o motivo da contestação</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Descreva detalhadamente o que está incorreto..."
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                rows={4}
              />
              <div className="flex gap-2">
                <Button onClick={() => setAcao(null)} variant="outline" className="flex-1">Voltar</Button>
                <Button onClick={handleContestar} disabled={submitting || motivo.length < 10} className="flex-1 bg-red-600">
                  {submitting ? 'Enviando...' : 'Registrar Contestação'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {espelho.status !== 'PENDENTE' && !acao && (
          <Card>
            <CardHeader className="text-center">
              {espelho.status === 'ASSINADO' && (
                <><CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" /><CardTitle>Este espelho já foi assinado</CardTitle></>
              )}
              {espelho.status === 'CONTESTADO' && (
                <><XCircle className="h-12 w-12 text-red-500 mx-auto mb-2" /><CardTitle>Este espelho foi contestado</CardTitle></>
              )}
              {espelho.status === 'EXPIRADO' && (
                <><AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-2" /><CardTitle>Prazo de assinatura expirado</CardTitle></>
              )}
            </CardHeader>
          </Card>
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
